

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { AppErrorMessagesComponent } from 'src/app/base/error-messages/app-error-messages.component';
import { ClientDTO, Role, SubscriptionDTO } from 'src/app/base/models/models.swagger.generated';
import { UserEditFormType } from './user-edit-form-type';
import { CheckboxModule } from 'primeng/checkbox';
import { CommonModule } from '@angular/common';
import { Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-edit-form',
  templateUrl: './user-edit-form.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    InputTextModule,
    DropdownModule,
    AppErrorMessagesComponent,
    CheckboxModule,
    CommonModule,
    FormsModule
  ]
})
export class UserEditFormComponent implements OnInit, OnDestroy {
  @Input() form!: FormGroup<UserEditFormType>;
  @Input() clients: ClientDTO[] = [];
  @Input() roles: { label: string; value: Role }[] = [];
  @Input() subscriptions: SubscriptionDTO[] = [];
  @Input() showRoleDropdown = false;
  @Input() showClientAdminCheckbox = false;
  @Input() showDeliveryExecCheckbox = false;
  @Input() showSubscriptionDropdown = false;
  @Input() currentSubscriptionName?: string;


  @Input() originalRoles: Role[] = [];


  deliveryExecRemovalWarning = false;

  private _sub = new Subscription();

  get formControls() {
    return this.form.controls;
  }

  get isAdminRoleSelected(): boolean {
    const selected = this.formControls.roles.value;
    return Array.isArray(selected) && selected.includes(Role.Admin);
  }


  get isDeliveryExecCurrentlyChecked(): boolean {
    return this.formControls.roles.value?.includes(Role.DeliveryExecutive) ?? false;
  }

  ngOnInit(): void {

   this.formControls.firstname.setValidators([Validators.maxLength(50)]);
  this.formControls.lastname.setValidators([Validators.maxLength(50)]);
  this.formControls.email.setValidators([
    Validators.maxLength(100),
    Validators.email
  ]);
  this.formControls.phoneNumber.setValidators([
    Validators.maxLength(12),
    Validators.pattern(/^[0-9]{12}$/)
  ]);

    this._sub.add(
      this.formControls.roles.valueChanges.subscribe(() => {
        this.updateClientIdValidator();
        this.updateDeliveryExecWarning();
      })
    );

    this.updateClientIdValidator();
    this.updateDeliveryExecWarning();
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  onRoleChange(selectedRole: Role): void {
    this.formControls.roles.setValue([selectedRole]);
    this.formControls.roles.markAsDirty();
    this.formControls.roles.markAsTouched();

    if (selectedRole === Role.Admin) {
      this.formControls.clientId.setValue(null);
    }
  }

  toggleClientAdmin(checked: boolean): void {
    const control = this.formControls.roles;
    const roles = control.value ?? [];

    if (checked) {
      if (!roles.includes(Role.ClientAdmin)) {
        control.setValue([...roles, Role.ClientAdmin]);
      }
    } else {
      control.setValue(roles.filter(role => role !== Role.ClientAdmin));
    }

    control.markAsDirty();
    control.markAsTouched();
  }

  toggleDeliveryExec(checked: boolean): void {
    const control = this.formControls.roles;
    const roles = control.value ?? [];

    if (checked) {
      if (!roles.includes(Role.DeliveryExecutive)) {
        control.setValue([...roles, Role.DeliveryExecutive]);
      }
    } else {
      control.setValue(roles.filter(role => role !== Role.DeliveryExecutive));
    }

    control.markAsDirty();
    control.markAsTouched();
  }

  onPhoneKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ?? event.keyCode;

    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }

    const current = this.formControls.phoneNumber.value ?? '';
    if (current.length >= 12) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  onPhonePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    const digitsOnly = pasted.replace(/\D/g, '').slice(0, 10);
    this.formControls.phoneNumber.setValue(digitsOnly);
    this.formControls.phoneNumber.markAsDirty();
  }

  private updateClientIdValidator(): void {
    const clientIdControl = this.formControls.clientId;
    if (this.isAdminRoleSelected) {
      clientIdControl.clearValidators();
    } else {
      clientIdControl.setValidators([Validators.required]);
    }
    clientIdControl.updateValueAndValidity({ emitEvent: false });
  }


  private updateDeliveryExecWarning(): void {
    const originallyHadRole = this.originalRoles.includes(Role.DeliveryExecutive);
    const currentlyHasRole = this.isDeliveryExecCurrentlyChecked;
    this.deliveryExecRemovalWarning = originallyHadRole && !currentlyHasRole;
  }
}